import * as Sentry from '@sentry/browser';

const INITIAL_PAGE_EXCEEDED_FINGERPRINT = 'initial-page-exceeded';
const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

// Sentry groups reports by endpoint, so resolved ids are swapped for a placeholder to keep one issue per route
const toRouteTemplate = (endpoint: string) => endpoint.replace(UUID_PATTERN, ':id');

// Remounts and refetches re-run the same request, so each route is reported once per page load to spare the quota
const reportedRoutes = new Set<string>();

export const resetReportedRoutes = () => reportedRoutes.clear();

interface WarnIfExceedsInitialPageParams {
  endpoint: string;
  count: number;
  pageSize: number;
}

// Chunked requests size their first page to hold every row for our largest orgs (GE-5138). A larger count
// still loads over extra chunks, but means the first page size needs raising.
export function warnIfExceedsInitialPage({ endpoint, count, pageSize }: WarnIfExceedsInitialPageParams) {
  if (count <= pageSize) {
    return;
  }

  const route = toRouteTemplate(endpoint);

  if (reportedRoutes.has(route)) {
    return;
  }

  reportedRoutes.add(route);
  Sentry.captureMessage(`Row count for ${route} exceeds its initial page size`, {
    extra: { count, pageSize },
    fingerprint: [INITIAL_PAGE_EXCEEDED_FINGERPRINT, route],
    level: 'warning',
    tags: { endpoint: route },
  });
}
